export enum BreakpointDirection {
    Min = 'min',
    Max = 'max',
}

export enum Breakpoint {
    SM = 576,
    MD = 743,
    LM = 800,
    L = 992,
    XL = 1200,
    XXL = 1600,
}

// Web responsive horizontal padding configuration
// Centralized breakpoints and values for consistent spacing across web sections
export const WEB_RESPONSIVE_PADDING = {
    default: '4%',
    breakpoints: [
        { maxWidth: Breakpoint.SM - 1, padding: 20 },
        { minWidth: Breakpoint.SM, maxWidth: Breakpoint.L, padding: 32 },
        { minWidth: Breakpoint.L + 1, maxWidth: Breakpoint.XL, padding: 48 },
        { minWidth: Breakpoint.XL + 1, maxWidth: Breakpoint.XXL, padding: 64 },
    ],
} as const;

type PaddingStyle = {
    paddingLeft: number | string;
    paddingRight: number | string;
    boxSizing: 'border-box';
};
type MediaQueryStyles = Record<string, PaddingStyle>;

/**
 * Generate web responsive padding styles with media queries
 * @param mode - 'horizontal': both left and right padding, 'left': only left padding (right=0)
 */
export const getWebResponsivePadding = (mode: 'horizontal' | 'left' = 'horizontal') => {
    const rightValue = mode === 'horizontal' ? WEB_RESPONSIVE_PADDING.default : 0;

    const baseStyles = {
        paddingLeft: WEB_RESPONSIVE_PADDING.default,
        paddingRight: rightValue,
    };

    const mediaQueries = WEB_RESPONSIVE_PADDING.breakpoints.reduce((acc, bp) => {
        const hasMin = 'minWidth' in bp;
        const hasMax = 'maxWidth' in bp;

        let mediaQuery: string;
        if (hasMin && hasMax) {
            mediaQuery = `@media (min-width: ${
                (bp as { minWidth: number }).minWidth
            }px) and (max-width: ${(bp as { maxWidth: number }).maxWidth}px)`;
        } else if (hasMax) {
            mediaQuery = `@media (max-width: ${(bp as { maxWidth: number }).maxWidth}px)`;
        } else {
            mediaQuery = `@media (min-width: ${(bp as { minWidth: number }).minWidth}px)`;
        }

        acc[mediaQuery] = {
            paddingLeft: bp.padding,
            paddingRight: mode === 'horizontal' ? bp.padding : 0,
            boxSizing: 'border-box',
        };
        return acc;
    }, {} as MediaQueryStyles);

    return {
        ...baseStyles,
        ...mediaQueries,
    };
};

/**
 * Get the current web responsive padding value based on screen width
 * Used for components that need to match the SectionWrapper padding (e.g., carousel offset)
 * @param screenWidth - Current window width
 * @returns Current padding value in pixels or percentage string
 */
export const getCurrentWebPadding = (screenWidth: number): number => {
    // Handle invalid/undefined screen width (SSR, tests)
    if (!screenWidth || Number.isNaN(screenWidth)) {
        return 20; // Default to smallest breakpoint padding
    }

    // Find matching breakpoint
    const matchingBreakpoint = WEB_RESPONSIVE_PADDING.breakpoints.find((bp) => {
        const hasMin = 'minWidth' in bp;
        const hasMax = 'maxWidth' in bp;

        if (hasMax && screenWidth <= (bp as { maxWidth: number }).maxWidth) {
            if (!hasMin) return true;
            if (screenWidth >= (bp as { minWidth: number }).minWidth) return true;
        }
        return false;
    });

    if (matchingBreakpoint) {
        return matchingBreakpoint.padding;
    }

    // Default for very large screens - convert 4% to pixels
    return Math.round(screenWidth * 0.04);
};
